import { useSearchParams, Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, ArrowRight, ChevronRight, SlidersHorizontal } from "lucide-react";
import { collections, categories } from "../data/site";
import { Reveal } from "../components/Reveal";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const currentCat = params.get("kategori") || "all";
  const [q, setQ] = useState(params.get("q") || "");
  const [surface, setSurface] = useState("all");

  const setCat = (slug) => {
    if (slug === "all") params.delete("kategori");
    else params.set("kategori", slug);
    setParams(params, { replace: true });
  };

  const filtered = useMemo(() => {
    return collections.filter((c) => {
      if (currentCat !== "all" && c.category !== currentCat) return false;
      if (
        q &&
        !`${c.name} ${c.color} ${c.tags.join(" ")}`
          .toLowerCase()
          .includes(q.toLowerCase())
      )
        return false;
      if (surface !== "all" && c.finish !== surface) return false;
      return true;
    });
  }, [currentCat, q, surface]);

  const surfaces = ["all", ...Array.from(new Set(collections.map((c) => c.finish)))];

  return (
    <div data-testid="products-page" className="pb-24 bg-[#FAFAFA]">
      {/* Visual animated header */}
      <section className="relative pt-32 pb-16 md:pb-24 bg-[#1E1E1E] text-white overflow-hidden">
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 8, ease: "easeOut" }}
          className="absolute inset-0"
        >
          <img
            src="/catalog/calacatta-hero.jpg"
            alt=""
            className="w-full h-full object-cover opacity-40"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#1E1E1E]/85 via-[#1E1E1E]/50 to-[#1E1E1E]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E1E1E]/60 to-transparent" />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          {/* breadcrumb */}
          <div className="flex items-center gap-2 text-[11px] tracking-widest uppercase text-white/50 mb-10">
            <Link to="/" className="hover:text-[#B8860B]">
              Ana Sayfa
            </Link>
            <ChevronRight size={12} />
            <span className="text-white">Ürünler</span>
            {currentCat !== "all" && (
              <>
                <ChevronRight size={12} />
                <span className="text-[#B8860B]">
                  {categories.find((c) => c.slug === currentCat)?.name}
                </span>
              </>
            )}
          </div>

          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-5"
            >
              <span className="w-8 h-[1px] bg-[#B8860B]" />
              Katalog
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05]"
            >
              Ürün kataloğomuzda arama yapın
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-5 text-[15px] text-white/70 max-w-md leading-relaxed"
            >
              Seçkin seramik ve porselen koleksiyonlarımızı keşfedin.
            </motion.p>

            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="origin-left mt-8 h-[1px] w-24 bg-[#B8860B]"
            />

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-8 flex flex-wrap gap-4"
            >
              {[
                { value: collections.length, label: "Koleksiyon" },
                { value: categories.length, label: "Kategori" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="px-5 py-3 border border-white/10 bg-white/5 backdrop-blur-sm"
                >
                  <div className="text-2xl font-light text-white">{stat.value}</div>
                  <div className="text-[10px] tracking-widest uppercase text-white/50">
                    {stat.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Filters */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-32 space-y-8">
              <div className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#888]">
                <SlidersHorizontal size={14} /> Filtrele
              </div>

              <div>
                <label className="sp-label">Ara</label>
                <div className="relative">
                  <input
                    type="text"
                    data-testid="products-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Ürün ismi, renk..."
                    className="sp-input pr-6"
                  />
                  <Search
                    size={14}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-[#888]"
                  />
                </div>
              </div>

              <div>
                <label className="sp-label">Yüzey</label>
                <div className="flex flex-wrap gap-1.5">
                  {surfaces.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSurface(s)}
                      data-testid={`filter-surface-${s}`}
                      className={`text-[11px] uppercase tracking-wider px-3 py-1.5 border transition-colors ${
                        surface === s
                          ? "border-[#1E1E1E] bg-[#1E1E1E] text-white"
                          : "border-[#1E1E1E]/15 text-[#666] hover:border-[#B8860B]"
                      }`}
                    >
                      {s === "all" ? "Hepsi" : s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Grid */}
          <div className="lg:col-span-9">
            <div className="flex justify-between items-center mb-6 text-[12px] text-[#666]">
              <span data-testid="products-count">
                <strong className="text-[#1E1E1E]">{filtered.length}</strong> ürün
                gösteriliyor
              </span>
            </div>
            {filtered.length === 0 ? (
              <div className="py-24 text-center text-[#888]">
                Filtre kriterlerinize uygun ürün bulunamadı.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-5">
                {filtered.map((c, i) => (
                  <Reveal key={c.slug} delay={i * 0.03}>
                    <Link
                      to={`/urunler/${c.slug}`}
                      data-testid={`product-card-${c.slug}`}
                      className="group block"
                    >
                      <div className="clip-frame aspect-[4/5] bg-[#F5F5F5] overflow-hidden">
                        <img
                          src={c.hero}
                          alt={c.name}
                          loading="lazy"
                          className="w-full h-full object-cover img-zoom"
                        />
                      </div>
                      <div className="pt-4 flex items-start justify-between">
                        <div>
                          <div className="text-[10px] tracking-[0.25em] uppercase text-[#888]">
                            {c.finish} · {c.color}
                          </div>
                          <h3 className="mt-1 text-lg font-medium text-[#1E1E1E] group-hover:text-[#B8860B] transition-colors">
                            {c.name}
                          </h3>
                          <div className="mt-1 text-[12px] text-[#666]">
                            {c.sizes[0]}
                          </div>
                        </div>
                        <ArrowRight
                          size={14}
                          className="mt-2 text-[#888] group-hover:text-[#B8860B] transition-all group-hover:translate-x-1"
                        />
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
